import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types";
import { formatCount } from "@/utils/format";

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (videos.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % videos.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [videos.length]);

  if (videos.length === 0) return null;

  const go = (dir: number) => {
    setCurrent((c) => (c + dir + videos.length) % videos.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-foreground-950">
      {videos.map((video, idx) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={video.cover}
            alt={video.title}
            title={`${video.title} - 精选推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30" />

      <div className="relative z-10 w-full h-full px-4 md:px-6 flex flex-col justify-end pb-16 md:pb-24">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-primary-950 text-xs font-bold">
              精选推荐
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 backdrop-blur-sm text-background-50 text-xs font-medium border border-background-50/20">
              {videos[current].categoryName}
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight tracking-tight">
            {videos[current].title}
          </h2>
          <p className="mt-3 md:mt-4 text-sm md:text-lg text-background-100/90 clamp-2 max-w-xl">
            {videos[current].subtitle}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-background-100/75">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-circle-line text-base" />
              </span>
              {formatCount(videos[current].plays)} 次播放
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-base" />
              </span>
              {videos[current].duration}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-star-line text-base" />
              </span>
              {videos[current].uploader}
            </span>
          </div>
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${videos[current].id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-bold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-play-fill text-xl" />
              </span>
              立即观看
            </Link>
            <Link
              to={`/category/${videos[current].categoryId}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 backdrop-blur-sm border border-background-50/30 text-background-50 text-sm font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多{videos[current].categoryName}
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/25 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/25 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {videos.map((video, idx) => (
          <button
            key={video.id}
            type="button"
            onClick={() => setCurrent(idx)}
            aria-label={`切换到第 ${idx + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === current ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}